// In Graphic.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux'; // Keep standard useSelector
import { Grid, Typography, CircularProgress, Box } from '@mui/material'; // Keep MUI Grid
import { RootState } from '../../../../reduxService';
import { IGeogridItem } from '../../../api/IApiResponse'; // Adjust path if needed
import CustomTable from '../../../components/Layout/CustomTable/CustomTable';
import { useTranslation } from 'react-i18next';
import { ProductDataService } from '../../../services/ProductDataService'; // Import product data service
import { getTensileAtStrain, findOptimalProductForFamily } from '../../../services/TensileAtStrength'; // Import functions
import { selectGraphicPreviewData } from '../../../Dynamic/store/project/componentiGraficiSlice';
import { calculateGeogridRequiredStrengthUnified, calculateGeogridStrengthBreakdownUnified } from '../../../services/GeogridTtotService'; // Import the updated calculation service

// Import the Konva component
import GeoGridLayers from './GeoGridLayers'; // Adjust path if needed

import './Graphic.scss';
import gridImage from '../../../images/grid_new.png'; // Background image
import { IGraphicProps } from './IGraphic'; // Props for Graphic component itself
import { conversionLabel } from '../../../conversion'; // Import conversion labels for unit display

export const Graphic = (props: IGraphicProps) => {
    const location = useLocation();
    const inResults = location.pathname.includes('/geogrid-results/');
    const { t } = useTranslation();
    //const output = useSelector((state: RootState) => state.Dynamic.results.data?.outputData);
    const graphicPreviewData = useSelector(selectGraphicPreviewData);
    const projectData = useSelector((state: RootState) => state.Dynamic.project?.data); // Esempio selettore più generico
    const output = useSelector((state: RootState) => state.Dynamic.results.data?.outputData);

    // Reason: Simple 300ms delay to prevent canvas flicker when navigating from Results page
    // This ensures the correct geogridType is loaded before rendering the canvas
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialLoadComplete(true);
        }, 300);
        return () => clearTimeout(timer);
    }, []);

    // Estrai dati per il grafico da graphicPreviewData
    const surfaceThickness = graphicPreviewData?.surfaceLayer?.thickness ?? 0;
    const baseThickness = graphicPreviewData?.geogridBaseCourse?.thickness ?? 0;
    const subbaseThickness = graphicPreviewData?.geogridSubBaseCourse?.thickness ?? 0;
    const baseGeogrids = graphicPreviewData?.geogridBaseCourse?.geogrids ?? [];
    const subbaseGeogrids = graphicPreviewData?.geogridSubBaseCourse?.geogrids ?? [];
    const geogridType = graphicPreviewData?.type?.geogridType as 'Rectangular' | 'Circular' | 'Railway' | undefined;
    const projectType = projectData?.type;
    const projectUnit = projectData?.unit; // Get project unit

    // Reason: Canvas ready when initial load delay complete and valid geogridType exists
    const isCanvasReady = isInitialLoadComplete && geogridType !== undefined;

    // Reason: Railway projects should always use Railway-specific terminology (ballast/subballast)
    const isRailwayProject = projectType === 'railways' || geogridType === 'Railway';

    // Helper function to get the correct unit based on project unit system
    const getUnit = (unitArray: string[]) => {
        return projectUnit === 'Imperial' ? unitArray[1] : unitArray[0];
    };

    // Helper function to enrich geogrid items with auto-selected product names for canvas display
    const enrichGeogridsWithAutoSelect = (geogrids: IGeogridItem[], courseType: 'base' | 'subbase'): IGeogridItem[] => {
        const productData = ProductDataService.getAllProducts();
        const projectGeoGridData = projectData?.projectInfo?.data?.geoGrid;

        return geogrids.map((geogrid, index) => {
            // If product is already selected, return as-is
            if (geogrid.selectedGeogrid) {
                return geogrid;
            }

            // Try auto-selection
            const productFamily = geogrid.type;
            const designStrain = geogrid.designStrain;
            const calculatedRequiredStrength = calculateGeogridRequiredStrengthUnified(projectGeoGridData, index, courseType);

            if (productFamily && calculatedRequiredStrength !== null) {
                const autoSelected = findOptimalProductForFamily(productData, productFamily, designStrain, calculatedRequiredStrength);
                if (autoSelected) {
                    // Return enriched geogrid with auto-selected product name (with indicator)
                    return {
                        ...geogrid,
                        selectedGeogrid: `${autoSelected.productName} (${t('auto_selected', 'auto')})`,
                    };
                }
            }

            return geogrid;
        });
    };

    // Enrich geogrid arrays with auto-selected products for canvas display
    const enrichedBaseGeogrids = useMemo(() => enrichGeogridsWithAutoSelect(baseGeogrids, 'base'), [baseGeogrids, projectData, t]);

    const enrichedSubbaseGeogrids = useMemo(() => enrichGeogridsWithAutoSelect(subbaseGeogrids, 'subbase'), [subbaseGeogrids, projectData, t]);

    const formatCourseDataForTable = (
        courseTitleKey: string, // Translation key for the course name (e.g., "Base Course")
        thickness: number,
        geogrids: IGeogridItem[] // Array of geogrids for the course
    ): (string | React.ReactNode)[][] => {
        const tableData: (string | React.ReactNode)[][] = [];
        const courseName = t(courseTitleKey);

        tableData.push([
            t('course_thickness', { course: courseName }) || `${courseName} Thickness`,
            't',
            thickness.toFixed(2), // Format number to string
            getUnit(conversionLabel.m_ft), // Dynamic unit based on project settings
        ]);

        //tableData.push([t('graphic.table.num_geogrids', { course: courseName }) || `Number of Geogrids`, 'N', geogrids.length.toString(), '']);

        geogrids.forEach((geogrid, index) => {
            const geogridNum = index + 1;
            const productFamily = geogrid.type;
            const productName = geogrid.selectedGeogrid;
            const designStrain = geogrid.designStrain;

            // Recupera la resistenza richiesta usando il calcolo B96 dalla formula Excel
            // Determina il course type basato sul courseTitleKey
            const courseType: 'base' | 'subbase' = courseTitleKey.includes('geogrid_base_course') ? 'base' : 'subbase';
            const projectGeoGridData = projectData?.projectInfo?.data?.geoGrid;

            // Calculate required strength using the Ttot formula (B96, C96, D96, etc.)
            // Use unified function that automatically detects Rectangular vs Circular geometry

            const calculatedRequiredStrength = calculateGeogridRequiredStrengthUnified(projectGeoGridData, index, courseType);

            // Get product data for lookups
            const productData = ProductDataService.getAllProducts();

            // Try explicit product first
            let calculatedNominalStrength = getTensileAtStrain(productData, productFamily, productName, designStrain);
            let displayProductName = productName;
            let isAutoSelected = false;

            // Auto-select if no product but family selected
            if (calculatedNominalStrength === null && productFamily && calculatedRequiredStrength !== null) {
                const autoSelected = findOptimalProductForFamily(productData, productFamily, designStrain, calculatedRequiredStrength);
                if (autoSelected) {
                    calculatedNominalStrength = autoSelected.tensileStrength;
                    displayProductName = autoSelected.productName;
                    isAutoSelected = true;
                }
            }

            // Fallback for required strength if Ttot formula fails
            const fallbackRequiredStrength = getTensileAtStrain(productData, productFamily, displayProductName, designStrain);

            // Get tensile breakdown (Tz, Tp, Tm)
            const breakdown = calculateGeogridStrengthBreakdownUnified(projectGeoGridData, index, courseType);

            // Use calculated values, then fallback
            const numericReqStrength = calculatedRequiredStrength ?? fallbackRequiredStrength;
            const numericNomStrength = calculatedNominalStrength;

            const displayRequiredStrength = numericReqStrength?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A';
            const displayNominalStrength = numericNomStrength?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A';

            // Product name with auto indicator
            const productNameDisplay = displayProductName ? (isAutoSelected ? `${displayProductName} (${t('auto_selected', 'auto')})` : displayProductName) : t('common.not_available', 'N/A');

            // Reason: Split description and value for product rows (description in first column, value spanning 3 columns)
            tableData.push([t('graphic.table.geogrid_product', { num: geogridNum }) || `Geogrid Product Name`, productNameDisplay]);
            tableData.push([
                t('geogrid_position', { num: geogridNum }) || `Geogrid ${geogridNum} Position`,
                <span key={`hb-symbol-${courseType}-${geogridNum}`}>
                    h<sub>b</sub>&apos;
                </span>,
                geogrid.position?.toFixed(2) || t('common.not_available', 'N/A'),
                getUnit(conversionLabel.m_ft),
            ]);
            tableData.push([
                t('geogrid_strain', { num: geogridNum }) || `Geogrid ${geogridNum} Design Strain`,
                <span key={`erb-symbol-${courseType}-${geogridNum}`}>
                    ε<sub>{`rb${geogridNum}`}</sub>
                </span>,
                (geogrid.designStrain?.toString() || t('common.not_available', 'N/A')) ?? 'N/A',
                '%',
            ]);
            // Tensile contribution breakdown rows
            tableData.push([
                t('graphic.table.geogrid_earth_pressure') || 'Earth Pressure Component',
                <span key={`tz-symbol-${courseType}-${geogridNum}`}>
                    T<sub>{`z${geogridNum}`}</sub>
                </span>,
                breakdown?.Tz?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A',
                getUnit(conversionLabel.kNm_lbfft),
            ]);
            tableData.push([
                t('graphic.table.geogrid_tension_component') || 'Geogrid Tension Component',
                <span key={`tp-symbol-${courseType}-${geogridNum}`}>
                    T<sub>{`p${geogridNum}`}</sub>
                </span>,
                breakdown?.Tp?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A',
                getUnit(conversionLabel.kNm_lbfft),
            ]);
            tableData.push([
                t('graphic.table.geogrid_membrane_effect') || 'Membrane Effect Component',
                <span key={`tm-symbol-${courseType}-${geogridNum}`}>
                    T<sub>{`m${geogridNum}`}</sub>
                </span>,
                breakdown?.Tm?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A',
                getUnit(conversionLabel.kNm_lbfft),
            ]);
            tableData.push([
                t('geogrid_req_strength', { num: geogridNum }) || `Geogrid ${geogridNum} Req. Tensile Strength`,
                <span key={`ttotb-symbol-${courseType}-${geogridNum}`}>
                    T<sub>{`totb${geogridNum}`}</sub>
                </span>,
                displayRequiredStrength,
                getUnit(conversionLabel.kNm_lbfft),
            ]);
            tableData.push([
                t('geogrid_nom_strength', { num: geogridNum }) || `Geogrid ${geogridNum} Nom. Tensile Strength`,
                <span key={`trb-symbol-${courseType}-${geogridNum}`}>
                    T<sub>{`rb${geogridNum}`}</sub>
                </span>,
                displayNominalStrength,
                getUnit(conversionLabel.kNm_lbfft),
            ]);
            let statusText: string;
            let statusRowMarker: string; // Prefisso per CustomTable per identificare lo stile

            if (numericNomStrength !== null && numericReqStrength !== null && numericNomStrength >= numericReqStrength) {
                statusText = t('geogrid_verified', 'Verified');
                statusRowMarker = 'VERIFIED_ROW::';
            } else if (numericNomStrength === null || numericReqStrength === null) {
                statusText = t('geogrid_data_missing_for_verification', 'Data Missing for Verification');
                statusRowMarker = 'WARNING_ROW::'; // Stile per dati mancanti/avviso
            } else {
                statusText = t('geogrid_not_verified', 'Not Verified');
                statusRowMarker = 'NOT_VERIFIED_ROW::';
            }

            tableData.push([`${statusRowMarker}${statusText}`]);
        });

        return tableData;
    };

    // Prepare data for CustomTable in the correct string[][] format
    // Use Railway-specific labels when projectType is 'railways' or geogridType is 'Railway'
    const baseCourseKey = isRailwayProject ? 'geogrid_base_course_railway' : 'geogrid_base_course';
    const subbaseCourseKey = isRailwayProject ? 'geogrid_subbase_course_railway' : 'geogrid_subbase_course';

    const formattedBaseCourseData = formatCourseDataForTable(baseCourseKey, baseThickness, baseGeogrids);

    const formattedSubbaseCourseData = formatCourseDataForTable(subbaseCourseKey, subbaseThickness, subbaseGeogrids);

    // Log extracted data for debugging
    useEffect(() => {
        // Data loaded and component will render with current project state
    }, [projectData, baseThickness, subbaseThickness, geogridType, projectUnit]);

    // saveCanvasAsImage logic needs rework for Konva stage if required later
    /*
    const saveCanvasAsImage = (stageRef: React.RefObject<Konva.Stage>) => {
         const stage = stageRef.current;
         if (stage) {
             const dataURL = stage.toDataURL(); // Use Konva's method
             localStorage.setItem('graphic', dataURL);
         }
    };
    */

    // Conditionally render the graphic only when not in results page
    // and potentially only if data is loaded (add loading state if needed)
    const shouldRenderGraphic = !inResults && projectData; // Rendi la condizione più specifica

    return (
        <Grid container id="container" spacing={2} style={{ background: `repeat url(${gridImage})` }}>
            <Grid
                item
                xs={12}
                md={12}
                id="canvas-container" // Changed ID slightly for clarity
                // ref={mountRef} // Ref not needed here for rendering
                style={{
                    minHeight: '500px', // Keep min height
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start', // Align items to top
                    paddingTop: '20px', // Add some padding
                }}>
                {isCanvasReady ? (
                    <GeoGridLayers
                        surfaceLayerThickness={surfaceThickness}
                        baseCourseThickness={baseThickness}
                        subbaseCourseThickness={subbaseThickness}
                        baseCourseGeogrids={enrichedBaseGeogrids}
                        subbaseCourseGeogrids={enrichedSubbaseGeogrids}
                        geogridType={geogridType}
                        unit={projectUnit}
                    />
                ) : (
                    <Box sx={{ minHeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                        <CircularProgress />
                    </Box>
                )}
            </Grid>
            {/* Maybe render something else if inResults or !projectData */}
            {!shouldRenderGraphic && (
                <Grid item xs={12} md={12} container spacing={6} style={{ marginTop: '20px' }}>
                    {/* Base Course Table - only show if thickness > 0 */}
                    {baseThickness > 0 && baseGeogrids.length > 0 && (
                        <Grid item xs={6}>
                            <Typography className="table-title">{t(baseCourseKey)}</Typography>
                            <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={formattedBaseCourseData} />
                        </Grid>
                    )}
                    {/* Subbase Course Table - only show if thickness > 0 */}
                    {subbaseThickness > 0 && subbaseGeogrids.length > 0 && (
                        <Grid item xs={6}>
                            <Typography className="table-title">{t(subbaseCourseKey)}</Typography>
                            <CustomTable columns={[t('output_description'), t('output_symbol'), t('output_value'), t('output_unit')]} data={formattedSubbaseCourseData} />
                        </Grid>
                    )}
                </Grid>
            )}
        </Grid>
    );
};

export default Graphic;
